/**
 * Sync-success alerts.
 * - Native: Push permission + Local Notifications (works offline / no FCM server)
 * - Web: Notification API fallback
 *
 * Assignment asks for Push Notifications; FCM remote push still needs Firebase.
 * Local notification delivers the sync-success UX on a real device today.
 */
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative } from './platform.js';

let pushReady = false;
let localReady = false;

export async function initNotifications() {
  if (!isNative()) {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch {
        /* ignore */
      }
    }
    return;
  }

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') {
      await PushNotifications.register();
      pushReady = true;
    }

    PushNotifications.addListener('registration', (token) => {
      console.info('[push] registration token', token.value);
    });
    PushNotifications.addListener('registrationError', (error) => {
      console.warn('[push] registration error', error);
    });
  } catch (error) {
    console.warn('Push Notifications chưa sẵn sàng (cần FCM trên Android).', error);
  }

  try {
    let localPerm = await LocalNotifications.checkPermissions();
    if (localPerm.display !== 'granted') {
      localPerm = await LocalNotifications.requestPermissions();
    }
    localReady = localPerm.display === 'granted';

    await LocalNotifications.createChannel({
      id: 'sync',
      name: 'Đồng bộ khảo sát',
      description: 'Thông báo khi đồng bộ thành công',
      importance: 4,
      visibility: 1,
      sound: 'default',
    });
  } catch (error) {
    console.warn('Local Notifications chưa sẵn sàng.', error);
  }
}

export async function notifySyncSuccess(syncedCount) {
  if (!syncedCount || syncedCount < 1) return;

  const title = 'Đồng bộ thành công';
  const body =
    syncedCount === 1
      ? '1 bản ghi khảo sát đã lên Google Sheets.'
      : `${syncedCount} bản ghi khảo sát đã lên Google Sheets.`;

  if (isNative()) {
    if (!localReady) {
      try {
        const localPerm = await LocalNotifications.requestPermissions();
        localReady = localPerm.display === 'granted';
      } catch {
        return;
      }
    }
    if (!localReady) return;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: Date.now() % 100000,
          title,
          body,
          channelId: 'sync',
          smallIcon: 'ic_stat_icon_config_sample',
        },
      ],
    });
    return;
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: './icons/icon-192.png' });
  }
}

export function getNotificationStatus() {
  return { pushReady, localReady };
}
